// DateRangeInput.tsx — с форматом DD.MM.YYYY для отображения

import React, { useState, useEffect } from 'react';
import './DateRangeInput.css';

interface DateRangeInputProps {
  value: string; // "startDate_endDate" в формате ISO: "2025-04-01_2025-04-10"
  onChange: (value: string) => void;
}

// Форматирует ISO-дату в DD.MM.YYYY
const formatDateForDisplay = (isoDate: string): string => {
  if (!isoDate) return '';
  const [year, month, day] = isoDate.split('-');
  return `${day}.${month}.${year}`;
};

// Парсит DD.MM.YYYY в ISO
const parseDateFromDisplay = (displayDate: string): string => {
  if (!displayDate) return '';
  const [day, month, year] = displayDate.split('.');
  return `${year}-${month}-${day}`;
};

/**
 * `АУД-Н153` (часть 2): дата в `YYYY-MM-DD` по **местному** календарю.
 *
 * `toISOString()` переводит местную полночь в UTC: к востоку от Гринвича она
 * попадает на предыдущий день, и начало диапазона уезжает на месяц назад —
 * при `TZ=Asia/Yekaterinburg` «01.09» превращалось в «2026-08-31». Собираем дату
 * из её же частей: браузеру отдаём то, что человек видит в поле.
 */
const toLocalIsoDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const DateRangeInput: React.FC<DateRangeInputProps> = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Инициализируем даты из value (в формате ISO)
  useEffect(() => {
    if (value && value.includes('_')) {
      const [start, end] = value.split('_');
      setStartDate(start);
      setEndDate(end);
    } else {
      // Устанавливаем значения по умолчанию: прошлый месяц
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now);
      end.setDate(now.getDate() - 1);

      const startStr = toLocalIsoDate(start);
      const endStr = toLocalIsoDate(end);

      setStartDate(startStr);
      setEndDate(endStr);
      onChange(`${startStr}_${endStr}`);
    }
  }, [value, onChange]);

  const handleApply = () => {
    if (startDate && endDate) {
      onChange(`${startDate}_${endDate}`);
    }
    setIsOpen(false);
  };

  // Отображаем в формате DD.MM.YYYY
  const displayStart = startDate ? formatDateForDisplay(startDate) : '';
  const displayEnd = endDate ? formatDateForDisplay(endDate) : '';
  const displayValue = displayStart && displayEnd
    ? `${displayStart} — ${displayEnd}`
    : 'Выберите диапазон';

  return (
    <div className="date-range-input">
      <div className="date-range-input__display" onClick={() => setIsOpen(!isOpen)}>
        {displayValue}
      </div>

      {isOpen && (
        <div className="date-range-input__popup">
          <div className="date-range-input__row">
            <label>Начало</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="date-range-input__row">
            <label>Конец</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div className="date-range-input__actions">
            <button className="btn-secondary" onClick={() => setIsOpen(false)}>
              Отмена
            </button>
            <button className="btn-primary" onClick={handleApply} disabled={!startDate || !endDate}>
              Применить
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeInput;
